import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { ReaderApi, ReaderContent } from "../../sdk/web";
import { emailCanvasColor } from "../../shared/email-canvas";
import { cleanSnippet } from "../../shared/quotes";
import type { Address, AttachmentInfo, MessageDetail, ScanResult } from "../../shared/types";
import {
  addLabel,
  assignMenu,
  deleteMessages,
  laterOptions,
  markFlagged,
  markMuted,
  markPinned,
  markSeen,
  moveToRole,
  notSpam,
  pickDateTime,
  REMIND_OPTIONS,
  remindIfNoReply,
  removeLabel,
  setVip,
  snooze,
} from "../actions";
import { ApiFailure, api, request } from "../api";
import { sameMessages, withBodies, withBody, withMeta } from "../bodies";
import { readerRemote } from "../clean-html";
import { defuseHtml, NO_DNS_PREFETCH } from "../defuse-html";
import { displayName, formatAddress, formatFullDate, formatRelative, formatSize } from "../format";
import { useLayout } from "../layout";
import {
  arrivedTotal,
  bodiesToLoad,
  expandAfterLoad,
  live,
  newInThread,
  newSinceLoad,
  olderLeftOut,
  shouldMarkSeen,
  touchesReader,
} from "../live";
import { useReaderBanners, useReaderMenu } from "../modules";
import { sendOrQueue } from "../offline";
import { overridable } from "../overrides";
import { hasOwnDesign, useLook, useThemed } from "../own-design";
import { printMessages } from "../print";
import { foldHtmlQuote, foldTextQuote, QUOTE_STYLE } from "../quote-html";
import { navigate } from "../router";
import { watchScans } from "../scans";
import {
  app,
  errorToast,
  mailChanged,
  messagesChanged,
  onMailChanged,
  openComposer,
  replyTo,
  toast,
  useFields,
  useStore,
} from "../store";
import { isThemePreview } from "../theme-preview";
import { AttachmentViewer, previewKind } from "./AttachmentViewer";
import { Avatar, AvatarLookup } from "./Avatar";
import { Icon } from "./Icon";
import { useEdgeBack } from "./Swipe";
import { Empty, IconButton, Menu, type MenuItem, Modal, Spinner } from "./ui";

export const Reader = ({
  openId,
  conversation,
  folderId,
  onClose,
  onRemoved,
}: {
  openId: string;
  conversation: boolean;
  folderId: string | null;
  onClose: () => void;
  onRemoved: (ids: string[]) => void;
}) => {
  const edgeBack = useEdgeBack(onClose);
  const layout = useLayout();
  const { me, labels, folders, mailbox, mailboxes } = useFields(
    app,
    "me",
    "labels",
    "folders",
    "mailbox",
    "mailboxes",
  );
  const activeBox = mailbox ? mailboxes.find((entry) => entry.id === mailbox) : undefined;
  const canAssign = activeBox !== undefined && activeBox.role !== "read";
  // Read-only members can't change mail: only the first load's GET marks it read for them.
  const canChange = !isThemePreview() && activeBox?.role !== "read";
  const [messages, setMessages] = useState<MessageDetail[] | null>(null);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Older messages of a long conversation the server left out.
  const [older, setOlder] = useState(0);
  const markAfter = me?.settings.markReadAfterSeconds ?? 0;

  const refreshedFor = useRef<string | null>(null);
  // Reloads when mail changes, unless the change was to other messages than these.
  const [reload, setReload] = useState(0);
  // Whether the next load may fetch only what changed besides the bodies (see lightLoad): null
  // until a reload is asked for, false once any reload asked for needs the bodies.
  const lightNext = useRef<boolean | null>(null);
  // The open message or conversation whose full load (with bodies) is on screen.
  const loadedFor = useRef<string | null>(null);
  const requestLoad = (light: boolean) => {
    lightNext.current = (lightNext.current ?? true) && light;
    setReload((count) => count + 1);
  };
  const shown = useRef<string[]>([]);
  // Every message this reader has shown, so undoing a removal from the conversation reloads it.
  const everShown = useRef(new Set<string>());
  // Messages whose images the person chose to show, so a printout shows them too.
  const [showing, setShowing] = useState<ReadonlySet<string>>(new Set());
  // What each message unlocked, so printing the conversation prints it, not the ciphertext.
  const [contentById, setContentById] = useState<ReadonlyMap<string, ReaderContent>>(new Map());
  const unlockedUrls = useRef(new Set<string>());
  useEffect(() => {
    const kept = new Set(
      [...contentById.values()]
        .flatMap((content) => content.attachments.map((attachment) => attachment.url))
        .filter((url) => url.startsWith("blob:")),
    );
    for (const url of unlockedUrls.current) if (!kept.has(url)) URL.revokeObjectURL(url);
    unlockedUrls.current = kept;
  }, [contentById]);
  useEffect(
    () => () => {
      for (const url of unlockedUrls.current) URL.revokeObjectURL(url);
    },
    [],
  );
  useEffect(
    () =>
      onMailChanged((ids) => {
        // A change to known messages (read, flag, pin, labels, a move) leaves the bodies as they
        // are; one that isn't known (sent mail, a change on another device) may not.
        if (touchesReader(ids, openId, everShown.current)) requestLoad(ids !== null);
      }),
    [openId],
  );
  // New mail arrives over the live connection as a fresh list, not as a change; a new message
  // in the open conversation shows up in it.
  const pushed = useStore(live, (state) => state.list);
  const pushedSeen = useRef(new Set<string>());
  // A reply can land outside the list on screen (the conversation was opened from Sent, a label
  // or search, or the reply went to another tab); the folder totals each update carries still
  // rise, so the conversation reloads once for it. No polling: at most one /threads per new id.
  const arrived = useStore(app, (state) => arrivedTotal(state.folders));
  const lastTotal = useRef<number | null>(null);
  // Which open id has had its first load. Only that load marks the conversation read; later
  // loads mark just the replies that are new since, so marking a message unread sticks.
  const markedFor = useRef<string | null>(null);
  // Messages the delayed mark-as-read already marked, so it doesn't mark them again after the
  // person marks one unread.
  const delayedDone = useRef(new Set<string>());
  // Bodies of a conversation too large to read whole that are loading on their own, those the
  // person opened (one open only because it's unread waits for a click), and a count that
  // moves as each load ends so the next one starts.
  const loadingBodies = useRef(new Set<string>());
  const [wantedBodies, setWantedBodies] = useState<ReadonlySet<string>>(new Set());
  const [bodyLoadsEnded, setBodyLoadsEnded] = useState(0);
  const bodyLoads = useRef(new AbortController());
  useEffect(() => {
    pushedSeen.current = new Set();
    everShown.current = new Set();
    delayedDone.current = new Set();
    bodyLoads.current.abort();
    bodyLoads.current = new AbortController();
    loadingBodies.current = new Set();
    setWantedBodies(new Set());
    lightNext.current = null;
    loadedFor.current = null;
    lastTotal.current = null;
    setOlder(0);
    setContentById(new Map());
  }, [openId]);
  useEffect(() => {
    const rose = lastTotal.current !== null && arrived > lastTotal.current;
    lastTotal.current = arrived;
    if (!conversation) return;
    if (newInThread(pushed?.data, openId, shown.current, pushedSeen.current)) {
      requestLoad(false);
    } else if (rose && shown.current.length > 0) {
      // Mail arrived somewhere: reload only if this conversation's newest message is new, and
      // once per such message (one the thread view never shows mustn't reload on every arrival).
      void api
        .get<{ id: string | null }>(`/threads/${encodeURIComponent(openId)}/latest`)
        .then((latest) => {
          if (!latest.id || everShown.current.has(latest.id) || pushedSeen.current.has(latest.id))
            return;
          pushedSeen.current.add(latest.id);
          requestLoad(false);
        })
        .catch(() => {});
    }
  }, [pushed, arrived]);
  /**
   * After a change to these messages' read state, flags, labels or folder: only what changed
   * besides the bodies, with no body read on the server, merged into what's on screen. When
   * mail arrived or left the conversation since, or the refresh fails, a full load follows.
   */
  const lightLoad = (path: string, signal: AbortSignal) => {
    let olderCount = 0;
    request<MessageDetail[] | MessageDetail>("GET", `${path}?markSeen=0&meta=1`, {
      signal,
      onHeaders: (headers) => {
        olderCount = olderLeftOut(headers);
      },
    })
      .then((data) => {
        const list = Array.isArray(data) ? data : [data];
        if (!sameMessages(list, shown.current)) {
          requestLoad(false);
          return;
        }
        setOlder(olderCount);
        setMessages((current) => (current && withMeta(current, list)) ?? current);
        setExpanded((current) => expandAfterLoad(current, list, []));
      })
      .catch((failure: unknown) => {
        if (failure instanceof DOMException) return;
        requestLoad(false);
      });
  };
  useEffect(() => {
    const controller = new AbortController();
    const path = conversation
      ? `/threads/${encodeURIComponent(openId)}`
      : `/messages/${encodeURIComponent(openId)}`;
    const loadKey = `${conversation ? "thread" : "message"}:${openId}`;
    const light = lightNext.current === true && loadedFor.current === loadKey;
    lightNext.current = null;
    if (light) {
      lightLoad(path, controller.signal);
      return () => controller.abort();
    }
    // Until this load is done, a change asks for a full one again (it replaces this one).
    loadedFor.current = null;
    const firstLoad = markedFor.current !== openId;
    // A read-only member of a shared mailbox reads it as it is; the server would not mark it.
    const markSeenNow = canChange && shouldMarkSeen(markAfter, firstLoad) ? "1" : "0";
    let olderCount = 0;
    request<MessageDetail[] | MessageDetail>("GET", `${path}?markSeen=${markSeenNow}`, {
      signal: controller.signal,
      onHeaders: (headers) => {
        olderCount = olderLeftOut(headers);
      },
    })
      .then((data) => {
        const list = Array.isArray(data) ? data : [data];
        // Replies that came in since the last load are marked read, as the first load would
        // have; the rest stay as the person left them.
        const arrived = firstLoad ? [] : newSinceLoad(list, everShown.current);
        const fresh = markAfter !== 0 || !canChange ? [] : arrived;
        markedFor.current = openId;
        loadedFor.current = loadKey;
        shown.current = list.map((message) => message.id);
        for (const id of shown.current) everShown.current.add(id);
        setOlder(olderCount);
        setMessages(
          fresh.length === 0
            ? list
            : list.map((message) =>
                fresh.includes(message.id) ? { ...message, seen: true } : message,
              ),
        );
        // Replies that arrived since open up as well: they're marked read, so must be seen.
        setExpanded((current) => expandAfterLoad(current, list, arrived));
        // The live connection brings the list and counts up to date; without it, refresh once.
        if (markSeenNow === "1" && !live.get().connected && refreshedFor.current !== openId) {
          refreshedFor.current = openId;
          // Only the list and counts: these messages came back already marked as read.
          messagesChanged([]);
        }
        // Straight to the server, not markSeen: that would report these ids as changed and
        // reload this conversation again. Only the list and counts need to catch up.
        if (fresh.length > 0)
          void sendOrQueue("PATCH", "/messages", {
            ids: fresh,
            threads: false,
            scopeFolderId: null,
            seen: true,
          })
            .then(() => messagesChanged([]))
            .catch(errorToast);
      })
      .catch(async (failure: unknown) => {
        if (failure instanceof DOMException) return;
        // Links made for the other view (older notifications, or after switching conversation
        // view) name a message where a conversation is expected: open its conversation instead.
        if (conversation && failure instanceof ApiFailure && failure.status === 404) {
          const message = await api
            .get<MessageDetail>(`/messages/${encodeURIComponent(openId)}`, controller.signal)
            .catch(() => null);
          if (controller.signal.aborted) return;
          if (message) {
            const at = location.hash.lastIndexOf("/");
            navigate(`${location.hash.slice(0, at)}/${encodeURIComponent(message.threadId)}`, true);
            return;
          }
        }
        setError(failure instanceof Error ? failure.message : String(failure));
      });
    return () => controller.abort();
  }, [openId, conversation, reload]);

  // Delayed mark-as-read, when the user asked for one.
  useEffect(() => {
    if (!messages || markAfter <= 0 || !canChange) return;
    const unseen = messages
      .filter((message) => !message.seen && !delayedDone.current.has(message.id))
      .map((message) => message.id);
    if (unseen.length === 0) return;
    const timer = setTimeout(() => {
      for (const id of unseen) delayedDone.current.add(id);
      void markSeen({ ids: unseen, threads: false, scopeFolderId: null }, true);
    }, markAfter * 1000);
    return () => clearTimeout(timer);
  }, [messages, markAfter, canChange]);

  // A conversation too large to read whole leaves some bodies out: each loads as it's opened,
  // a few at a time. One that fails waits for another click.
  useEffect(() => {
    if (!messages) return;
    const loading = loadingBodies.current;
    const { signal } = bodyLoads.current;
    for (const id of bodiesToLoad(messages, wantedBodies, loading)) {
      loading.add(id);
      api
        .get<MessageDetail>(`/messages/${encodeURIComponent(id)}`, signal)
        .then((loaded) =>
          setMessages(
            (current) =>
              current?.map((message) =>
                message.id === id && message.bodyOmitted ? withBody(message, loaded) : message,
              ) ?? null,
          ),
        )
        .catch((failure: unknown) => {
          if (signal.aborted) return;
          setWantedBodies((current) => withoutId(current, id));
          errorToast(failure);
        })
        .finally(() => {
          loading.delete(id);
          if (!signal.aborted) setBodyLoadsEnded((count) => count + 1);
        });
    }
  }, [messages, wantedBodies, bodyLoadsEnded]);

  if (error) {
    return (
      <section class="reader-pane" data-part="reader">
        <div class="reader-toolbar">
          <IconButton icon="chevronLeft" label="Back" onClick={onClose} />
        </div>
        <Empty icon="junk" title="Could not open this message" text={error} />
      </section>
    );
  }
  if (!messages) {
    return (
      <section class="reader-pane" data-part="reader">
        <Spinner />
      </section>
    );
  }
  const last = messages.at(-1);
  if (!last) return null;
  const ids = messages.map((message) => message.id);
  const target = { ids, threads: false, scopeFolderId: null };
  const folderRole = folders.find((folder) => folder.id === folderId)?.role ?? null;
  const allLabels = [...new Set(messages.flatMap((message) => message.labelIds))];
  const flagged = messages.some((message) => message.flagged);
  const pinned = messages.some((message) => message.pinned);
  const muted = last.muted;
  const ownAddresses = me?.identities.map((identity) => identity.address.toLowerCase()) ?? [];
  const loadRemoteFor = (message: MessageDetail) =>
    readerRemote(
      me?.settings.loadRemoteContent ?? "always",
      folders.find((folder) => folder.id === message.folderId)?.role === "junk",
      message.draft?.fromJunk,
    );
  // Bodies a conversation too large to read whole left out are loaded for the printout.
  const printAll = () =>
    printMessages(
      withBodies(messages, (id) =>
        api.get<MessageDetail>(`/messages/${encodeURIComponent(id)}`),
      ).then((full) =>
        full.map((message) => ({
          message,
          content: contentById.get(message.id),
          allowRemote: showing.has(message.id) || remoteAllowed(loadRemoteFor(message), message),
        })),
      ),
    ).catch(errorToast);
  // Follow-up reminders apply to the latest message this person sent in the conversation.
  const lastSent = messages.findLast(
    (message) =>
      !message.isDraft &&
      (ownAddresses.includes(message.from.address.toLowerCase()) ||
        folders.find((folder) => folder.id === message.folderId)?.role === "sent"),
  );
  const nudged = messages.find((message) => message.nudgedAt !== null);

  const inline = layout.toolbar === "inline";
  const toolbar = (
    <ReaderToolbar messages={messages} onClose={onClose} inline={inline}>
      <ReaderAction
        icon="chevronLeft"
        label="Back (u)"
        text="Back"
        class="back-button"
        onClick={onClose}
      />
      {folderRole !== "archive" && (
        <ReaderAction
          icon="archive"
          label="Archive (e)"
          text="Archive"
          onClick={() => {
            void moveToRole(target, "archive", folderId);
            onRemoved([last.id]);
          }}
        />
      )}
      <ReaderAction
        icon="trash"
        label="Delete (#)"
        text="Delete"
        onClick={() => {
          void deleteMessages(target, folderId);
          onRemoved(ids);
        }}
      />
      <ReaderAction
        icon="reply"
        label="Reply"
        text="Reply"
        onClick={() => replyTo("reply", last)}
      />
      <Menu
        align="right"
        submenuNavigation
        trigger={({ open, toggle }) => (
          <ReaderAction
            icon="more"
            label="More message actions"
            text="More"
            expanded={open}
            onClick={toggle}
          />
        )}
        items={() => [
          { label: "Reply all", icon: "replyAll", onSelect: () => replyTo("reply-all", last) },
          { label: "Forward", icon: "forward", onSelect: () => replyTo("forward", last) },
          { separator: true },
          {
            label: "Mark unread",
            icon: "mail",
            onSelect: () => void markSeen(target, false).then(onClose),
          },
          {
            label: flagged ? "Unflag" : "Flag",
            icon: "star",
            checked: flagged,
            onSelect: () => void markFlagged(target, !flagged),
          },
          {
            label: pinned ? "Unpin" : "Pin to top",
            icon: "pin",
            checked: pinned,
            onSelect: () => void markPinned(target, !pinned),
          },
          {
            label: muted ? "Unmute" : "Mute conversation",
            icon: "bellOff",
            checked: muted,
            onSelect: () => {
              void markMuted(target, !muted);
              if (!muted && folderRole === "inbox") onRemoved(ids);
            },
          },
          {
            label: folderRole === "junk" ? "Not spam" : "Report spam",
            icon: folderRole === "junk" ? "inbox" : "junk",
            onSelect: () => {
              if (folderRole === "junk") void notSpam(target);
              else void moveToRole(target, "junk", folderId);
              onRemoved(ids);
            },
          },
          { separator: true },
          {
            label: "Move to",
            icon: "folder",
            submenu: folders
              .filter((folder) => folder.id !== folderId && folder.role !== "drafts")
              .map((folder) => ({
                label: folder.name,
                icon: "folder",
                onSelect: async () => {
                  await sendOrQueue("PATCH", "/messages", { ids, folderId: folder.id }).catch(
                    errorToast,
                  );
                  mailChanged();
                  toast(`Moved to ${folder.name}`);
                  onRemoved(ids);
                },
              })),
          },
          {
            label: "Labels",
            icon: "tag",
            submenu:
              labels.length === 0
                ? [{ heading: "Create labels in the sidebar" }]
                : labels.map((label) => ({
                    label: label.name,
                    color: label.color,
                    checked: allLabels.includes(label.id),
                    onSelect: () =>
                      allLabels.includes(label.id)
                        ? removeLabel(target, label.id)
                        : addLabel(target, label.id),
                  })),
          },
          {
            label: "Snooze",
            icon: "alarm",
            submenu: [
              ...laterOptions().map((option) => ({
                label: option.label,
                icon: "alarm" as const,
                onSelect: () => {
                  void snooze(target, option.at);
                  onRemoved(ids);
                },
              })),
              {
                label: "Pick date and time…",
                icon: "calendar",
                onSelect: () => {
                  const at = pickDateTime("Snooze until");
                  if (at !== null) {
                    void snooze(target, at);
                    onRemoved(ids);
                  }
                },
              },
              ...(last.snoozedUntil
                ? [
                    { separator: true } as MenuItem,
                    {
                      label: "Unsnooze",
                      icon: "undo",
                      onSelect: () => void snooze(target, null),
                    } as MenuItem,
                  ]
                : []),
            ],
          },
          {
            label: "Print and export",
            icon: "print",
            submenu: [
              {
                label: messages.length > 1 ? "Print conversation" : "Print",
                icon: "print",
                onSelect: printAll,
              },
              {
                label: "Save as PDF",
                icon: "download",
                onSelect: () => {
                  toast('In the print window, choose "Save as PDF" as the printer');
                  printAll();
                },
              },
            ],
          },
          ...(lastSent
            ? [
                {
                  label: "Remind me if no reply",
                  icon: "reply",
                  submenu: [
                    ...REMIND_OPTIONS.map((option) => ({
                      label: option.label,
                      icon: "alarm" as const,
                      onSelect: () => void remindIfNoReply([lastSent.id], option.days),
                    })),
                    ...(lastSent.remindAt !== null
                      ? [
                          { separator: true } as MenuItem,
                          {
                            label: "Cancel reminder",
                            icon: "x",
                            onSelect: () => void remindIfNoReply([lastSent.id], null),
                          } as MenuItem,
                        ]
                      : []),
                  ],
                } as MenuItem,
              ]
            : []),
          ...(canAssign
            ? [
                {
                  label: "Assign",
                  icon: "user",
                  submenu: assignMenu([last.id], last.assignee),
                } as MenuItem,
              ]
            : []),
        ]}
      />
    </ReaderToolbar>
  );

  return (
    <section
      class="reader-pane"
      data-part="reader"
      aria-label="Conversation"
      onPointerDown={edgeBack.onPointerDown}
      onPointerMove={edgeBack.onPointerMove}
      onPointerUp={edgeBack.onPointerUp}
      onPointerCancel={edgeBack.onPointerCancel}
    >
      {!inline && toolbar}
      <div class="reader-scroll">
        <ReaderHeader
          subject={last.subject || "(no subject)"}
          messages={messages}
          toolbar={inline ? toolbar : null}
        >
          {activeBox && (
            <p class="thread-assignee">
              <Icon name="user" size={13} />
              {last.assignee
                ? `Assigned to ${last.assignee.id === me?.id ? "you" : last.assignee.name}`
                : "Not assigned"}
              <span class="muted"> · {activeBox.address}</span>
            </p>
          )}
          {lastSent?.remindAt && (
            <p class="thread-reminder">
              <Icon name="alarm" size={13} />
              Comes back to your Inbox on {formatFullDate(lastSent.remindAt)} if nobody replies
            </p>
          )}
          {nudged && (
            <p class="thread-reminder">
              <Icon name="alarm" size={13} />
              No reply since you sent this {formatRelative(nudged.date)}
            </p>
          )}
          {older > 0 && (
            <p class="thread-reminder">
              {older === 1
                ? "1 older message in this conversation isn't shown"
                : `${older} older messages in this conversation aren't shown`}
            </p>
          )}
          <div class="thread-labels">
            {allLabels.map((id) => {
              const label = labels.find((entry) => entry.id === id);
              return label ? (
                <span class="label-chip removable" key={id} style={{ "--chip": label.color }}>
                  {label.name}
                  <button
                    type="button"
                    aria-label={`Remove ${label.name}`}
                    onClick={() => void removeLabel(target, id)}
                  >
                    <Icon name="x" size={11} />
                  </button>
                </span>
              ) : null;
            })}
          </div>
        </ReaderHeader>
        {messages.map((message) => (
          <AvatarLookup.Provider
            key={message.id}
            value={
              folders.find((folder) => folder.id === message.folderId)?.role !== "junk" &&
              !message.draft?.fromJunk
            }
          >
            <MessageCard
              message={message}
              // An open message whose body isn't in yet shows collapsed: loading if the person
              // opened it, waiting for a click if it's open only because it's unread.
              expanded={(expanded.has(message.id) || messages.length === 1) && !message.bodyOmitted}
              body={
                message.bodyOmitted !== true || !expanded.has(message.id)
                  ? null
                  : wantedBodies.has(message.id) || messages.length === 1
                    ? "loading"
                    : "waiting"
              }
              ownAddresses={ownAddresses}
              loadRemote={loadRemoteFor(message)}
              onShowRemote={() => setShowing((current) => new Set(current).add(message.id))}
              onContent={(result) =>
                setContentById((current) => {
                  const next = new Map(current);
                  if (result) next.set(message.id, result);
                  else next.delete(message.id);
                  return next;
                })
              }
              onToggle={() => {
                const open = expanded.has(message.id);
                // A click on one waiting for its body loads it rather than closing it.
                if (open && message.bodyOmitted && !wantedBodies.has(message.id)) {
                  setWantedBodies((current) => new Set(current).add(message.id));
                  return;
                }
                setExpanded((current) => {
                  const next = new Set(current);
                  if (open) next.delete(message.id);
                  else next.add(message.id);
                  return next;
                });
                setWantedBodies((current) =>
                  open ? withoutId(current, message.id) : new Set(current).add(message.id),
                );
              }}
              onRemoved={onRemoved}
            />
          </AvatarLookup.Provider>
        ))}
        <div class="reply-bar" data-part="reply-bar">
          <button type="button" class="ghost" onClick={() => replyTo("reply", last)}>
            <Icon name="reply" /> Reply
          </button>
          <button type="button" class="ghost" onClick={() => replyTo("reply-all", last)}>
            <Icon name="replyAll" /> Reply all
          </button>
          <button type="button" class="ghost" onClick={() => replyTo("forward", last)}>
            <Icon name="forward" /> Forward
          </button>
        </div>
      </div>
    </section>
  );
};

/** Large labeled actions remain easy to distinguish on a narrow reader. */
const ReaderAction = ({
  icon,
  label,
  text,
  class: className,
  onClick,
  expanded,
}: {
  icon: import("./Icon").IconName;
  label: string;
  text: string;
  class?: string;
  onClick: () => void;
  expanded?: boolean;
}) => (
  <button
    type="button"
    class={`icon-button reader-action${className ? ` ${className}` : ""}`}
    title={label}
    aria-label={label}
    aria-haspopup={expanded !== undefined ? "menu" : undefined}
    aria-expanded={expanded}
    onClick={onClick}
  >
    <Icon name={icon} size={24} />
    <span class="reader-action-label">{text}</span>
  </button>
);

/** The conversation's actions: a bar across the reader, or a row under the subject. */
const ReaderToolbar = overridable("ReaderToolbar", ({ inline, children }) => (
  <div
    class={`reader-toolbar reader-action-toolbar${inline ? " inline" : ""}`}
    data-part="reader-toolbar"
  >
    {children}
  </div>
));

/** The subject, the toolbar when it sits under it, and notes such as reminders and labels. */
const ReaderHeader = overridable("ReaderHeader", ({ subject, toolbar, children }) => (
  <header class="thread-header" data-part="reader-header">
    <h1>{subject}</h1>
    {toolbar}
    {children}
  </header>
));

const AddressLine = ({ label, list }: { label: string; list: Address[] }) =>
  list.length === 0 ? null : (
    <div class="address-line">
      <span class="muted">{label}</span>{" "}
      {list.map((address, index) => (
        <span key={address.address} title={address.address}>
          {displayName(address)}
          {index < list.length - 1 ? ", " : ""}
        </span>
      ))}
    </div>
  );

const AuthWarning = ({ message }: { message: MessageDetail }) => {
  const { spf, dkim, dmarc } = message.auth;
  if (dmarc === "fail" || (spf === "fail" && dkim !== "pass")) {
    return (
      <div class="banner danger">
        <Icon name="shield" size={16} /> This message failed sender checks (SPF {spf}, DKIM {dkim},
        DMARC {dmarc}). It may not be from {message.from.address}.
      </div>
    );
  }
  return null;
};

const SpamExplanation = ({
  message,
  onRemoved,
}: {
  message: MessageDetail;
  onRemoved: (ids: string[]) => void;
}) => {
  const inSpam = useStore(
    app,
    (state) => state.folders.find((folder) => folder.id === message.folderId)?.role === "junk",
  );
  const [open, setOpen] = useState(false);
  if (!inSpam) return null;
  const reasons = message.spamReasons.filter(
    (entry) => entry.points !== 0 || entry.code === "filter",
  );
  return (
    <div class="banner warning spam-banner">
      <div class="spam-banner-head">
        <Icon name="junk" size={16} />
        <span>
          This message is in Spam
          {message.spamScore !== null ? ` (spam score ${message.spamScore} of 100)` : ""}.
        </span>
        {reasons.length > 0 && (
          <button type="button" class="link" onClick={() => setOpen(!open)}>
            {open ? "Hide why" : "Why?"}
          </button>
        )}
        <button
          type="button"
          class="link"
          onClick={() => {
            void notSpam({ ids: [message.id], threads: false, scopeFolderId: null });
            onRemoved([message.id]);
          }}
        >
          Not spam
        </button>
      </div>
      {open && (
        <ul class="spam-reasons">
          {[...reasons]
            .sort((a, b) => b.points - a.points)
            .map((entry) => (
              <li key={entry.code + entry.label}>
                <span class={entry.points > 0 ? "points bad" : "points good"}>
                  {entry.points > 0 ? `+${entry.points}` : entry.points}
                </span>
                {entry.label}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

const withoutId = (ids: ReadonlySet<string>, id: string): ReadonlySet<string> => {
  if (!ids.has(id)) return ids;
  const next = new Set(ids);
  next.delete(id);
  return next;
};

/** Whether a message's remote images load without asking. */
const remoteAllowed = (loadRemote: "never" | "contacts" | "always", message: MessageDetail) =>
  loadRemote === "always" || (loadRemote === "contacts" && message.trustedSender);

const MessageCard = ({
  message,
  expanded,
  body,
  ownAddresses,
  loadRemote,
  onShowRemote,
  onContent,
  onToggle,
  onRemoved,
}: {
  message: MessageDetail;
  expanded: boolean;
  /**
   * For an open message whose body the conversation left out: loading, or waiting for a click
   * (open only because it's unread).
   */
  body: "loading" | "waiting" | null;
  ownAddresses: string[];
  loadRemote: "never" | "contacts" | "always";
  onShowRemote: () => void;
  onContent: (result: ReaderContent | null) => void;
  onToggle: () => void;
  onRemoved: (ids: string[]) => void;
}) => {
  const [showRemote, setShowRemoteState] = useState(remoteAllowed(loadRemote, message));
  const setShowRemote = (show: true) => {
    setShowRemoteState(show);
    onShowRemote();
  };
  const [details, setDetails] = useState(false);
  const banners = useReaderBanners();
  const menuParts = useReaderMenu();
  const target = { ids: [message.id], threads: false, scopeFolderId: null };
  const fromSelf = ownAddresses.includes(message.from.address.toLowerCase());

  if (!expanded) {
    return (
      <article
        class="message-card collapsed"
        data-part="message-card"
        aria-busy={body === "loading"}
        onClick={onToggle}
      >
        <Avatar person={message.from} size="small" />
        <strong>{fromSelf ? "me" : displayName(message.from)}</strong>
        <span class="muted snippet">
          {body === "loading"
            ? "Loading…"
            : `${body === "waiting" ? "Click to load · " : ""}${cleanSnippet(message.snippet, message.subject)}`}
        </span>
        <span class="muted date">{formatFullDate(message.date)}</span>
      </article>
    );
  }

  // What a module unlocked in place of the message's own content, such as decrypted text.
  const [content, setContent] = useState<ReaderContent | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  // What a module shows in place of the body, such as a translation.
  const [bodyOverride, setBodyOverride] = useState<ComponentChildren | null>(null);
  const reader: ReaderApi = {
    setBody: (id, body) => {
      if (id === message.id) setBodyOverride(body);
    },
    setContent: (id, replaced) => {
      if (id !== message.id) return;
      setContent(replaced);
      onContent(replaced);
    },
  };
  // The PGP/MIME plumbing parts are not attachments anyone wants to open.
  const visibleAttachments: AttachmentInfo[] = message.attachments.filter(
    (attachment) =>
      (!attachment.inline || !message.html?.includes(attachment.id)) &&
      !(
        message.pgp && /^application\/(pgp-encrypted|pgp-signature)$/i.test(attachment.contentType)
      ) &&
      !(message.pgp === "encrypted" && attachment.filename === "encrypted.asc"),
  );
  const previewables = visibleAttachments.filter((attachment) => previewKind(attachment) !== null);

  return (
    <article class="message-card" data-part="message-card">
      <header class="message-head">
        <Avatar person={message.from} onClick={onToggle} />
        <div class="message-meta" onClick={onToggle}>
          <div>
            <strong>{displayName(message.from)}</strong>{" "}
            <span class="muted">&lt;{message.from.address}&gt;</span>
            {message.vip && (
              <span class="vip-badge" title="VIP">
                <Icon name="crown" size={13} />
              </span>
            )}
            {message.unsubscribe && <UnsubscribeButton message={message} />}
          </div>
          <div class="muted small">
            to{" "}
            {[...message.to, ...message.cc]
              .map((address) =>
                ownAddresses.includes(address.address.toLowerCase()) ? "me" : displayName(address),
              )
              .join(", ") || "undisclosed recipients"}
            <button
              type="button"
              class="link small recipients-toggle"
              aria-label="Show details"
              aria-expanded={details}
              onClick={(event) => {
                event.stopPropagation();
                setDetails(!details);
              }}
            >
              <Icon name="chevronDown" size={12} />
            </button>
          </div>
        </div>
        <div class="message-actions">
          <span class="muted small" title={formatFullDate(message.date)}>
            {message.sendAt
              ? `Sends ${formatRelative(message.sendAt)}`
              : formatFullDate(message.date)}
          </span>
          <IconButton icon="reply" label="Reply" onClick={() => replyTo("reply", message)} />
          <Menu
            align="right"
            trigger={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}
            items={() => [
              {
                label: "Reply all",
                icon: "replyAll",
                onSelect: () => replyTo("reply-all", message),
              },
              {
                label: "Forward",
                icon: "forward",
                onSelect: () => replyTo("forward", message),
              },
              {
                label: "Edit as new",
                icon: "pencil",
                onSelect: () => replyTo("edit", message),
              },
              { separator: true },
              {
                label: "Mark unread from here",
                icon: "mail",
                onSelect: () => void markSeen(target, false),
              },
              {
                label: message.flagged ? "Unflag" : "Flag",
                icon: "star",
                onSelect: () => void markFlagged(target, !message.flagged),
              },
              ...menuParts.flatMap((items) => items(message)),
              {
                label: "Print",
                icon: "print",
                onSelect: () =>
                  printMessages([
                    { message, content: content ?? undefined, allowRemote: showRemote },
                  ]),
              },
              {
                label: "Save as PDF",
                icon: "download",
                onSelect: () => {
                  toast('In the print window, choose "Save as PDF" as the printer');
                  printMessages([
                    { message, content: content ?? undefined, allowRemote: showRemote },
                  ]);
                },
              },
              {
                label: "Make a rule from this…",
                icon: "filter",
                onSelect: () => {
                  app.set({
                    filterDraft: {
                      name: `From ${message.from.name || message.from.address}`,
                      enabled: true,
                      match: "all",
                      conditions: [{ field: "from", op: "is", value: message.from.address }],
                      actions: [{ type: "mark-read" }],
                      stop: false,
                    },
                  });
                  navigate("#/settings/filters");
                },
              },
              ...(message.messageId !== null || message.size > 0
                ? [
                    {
                      label: "View source",
                      icon: "code" as const,
                      onSelect: () =>
                        window.open(`/api/messages/${message.id}/raw`, "_blank", "noopener"),
                    },
                    {
                      label: "Download .eml",
                      icon: "download" as const,
                      onSelect: () =>
                        window.open(
                          `/api/messages/${message.id}/raw?download=1`,
                          "_blank",
                          "noopener",
                        ),
                    },
                  ]
                : []),
              { separator: true },
              ...(!fromSelf
                ? [
                    {
                      label: `Add ${message.from.address} to contacts`,
                      icon: "book" as const,
                      onSelect: async () => {
                        await api
                          .post("/contacts", {
                            name: message.from.name,
                            email: message.from.address,
                          })
                          .catch(errorToast);
                        toast("Added to contacts");
                      },
                    },
                    {
                      label: message.vip
                        ? `Remove ${message.from.address} from VIPs`
                        : `Mark ${message.from.address} as VIP`,
                      icon: "crown" as const,
                      onSelect: () => void setVip([message.from], !message.vip),
                    },
                    {
                      label: `Block ${message.from.address}`,
                      icon: "ban" as const,
                      danger: true,
                      onSelect: async () => {
                        if (
                          !window.confirm(`Send future mail from ${message.from.address} to Spam?`)
                        )
                          return;
                        await api
                          .post("/blocked", { pattern: message.from.address })
                          .catch(errorToast);
                        await moveToRole(target, "junk", message.folderId);
                        onRemoved([message.id]);
                      },
                    },
                  ]
                : []),
              {
                label: "Delete this message",
                icon: "trash",
                danger: true,
                onSelect: () => {
                  void deleteMessages(target, message.folderId);
                  onRemoved([message.id]);
                },
              },
            ]}
          />
        </div>
      </header>
      {details && (
        <div class="message-details">
          <AddressLine label="From:" list={[message.from]} />
          <AddressLine label="Reply-To:" list={message.replyTo} />
          <AddressLine label="To:" list={message.to} />
          <AddressLine label="Cc:" list={message.cc} />
          <AddressLine label="Bcc:" list={message.bcc} />
          <div>
            <span class="muted">Date:</span> {new Date(message.date).toString()}
          </div>
          {message.deliveredTo && (
            <div>
              <span class="muted">Delivered to:</span> {message.deliveredTo}
            </div>
          )}
          {message.auth.spf !== "unknown" && (
            <div>
              <span class="muted">Checks:</span> SPF {message.auth.spf} · DKIM {message.auth.dkim} ·
              DMARC {message.auth.dmarc}
            </div>
          )}
          {message.spamScore !== null && (
            <div>
              <span class="muted">Spam score:</span> {message.spamScore} of 100
            </div>
          )}
        </div>
      )}
      <AuthWarning message={message} />
      <SpamExplanation message={message} onRemoved={onRemoved} />
      {message.sendAt && (
        <div class="banner">
          <Icon name="clock" size={16} /> Scheduled for {formatFullDate(message.sendAt)}.
          <button
            type="button"
            class="link"
            onClick={async () => {
              await api.del(`/drafts/${message.id}/schedule`).catch(errorToast);
              mailChanged();
              replyTo("edit", message);
            }}
          >
            Cancel and edit
          </button>
        </div>
      )}
      {message.hasRemoteContent && !showRemote && (
        <div class="banner">
          <Icon name="image" size={16} /> Images are hidden.
          <button type="button" class="link" onClick={() => setShowRemote(true)}>
            Show images
          </button>
          {!message.trustedSender && !fromSelf && (
            <button
              type="button"
              class="link"
              onClick={async () => {
                await api
                  .post("/contacts", { name: message.from.name, email: message.from.address })
                  .catch(errorToast);
                setShowRemote(true);
                toast(`Images from ${message.from.address} will always show`);
              }}
            >
              Always show from this sender
            </button>
          )}
        </div>
      )}
      {(message.trackers.length > 0 || message.receiptRequested) && (
        <div class="tracker-note" title={message.trackers.join(", ")}>
          <Icon name="shield" size={14} />
          {[
            message.trackers.length === 1
              ? `Blocked a tracker from ${message.trackers[0]}`
              : message.trackers.length > 1
                ? `Blocked ${message.trackers.length} trackers`
                : "",
            message.receiptRequested
              ? message.trackers.length > 0
                ? "and a read-receipt request"
                : "Blocked a read-receipt request; the sender won't know you opened this"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        </div>
      )}
      {banners.map((Banner, index) => (
        <Banner key={index} message={message} reader={reader} />
      ))}
      {bodyOverride
        ? bodyOverride
        : !isBlank(content ?? message) && (
            <MessageBody message={message} allowRemote={showRemote} unlocked={content} />
          )}
      {content?.attachments.some((attachment) => !attachment.inline) && (
        <div class="attachments">
          <div class="attachments-head">
            {content.attachmentsIcon && <Icon name={content.attachmentsIcon} size={15} />}{" "}
            {content.attachmentsTitle ?? "Attachments"}
          </div>
          <div class="attachment-grid">
            {content.attachments
              .filter((attachment) => !attachment.inline)
              .map((attachment) => (
                <div class="attachment" key={attachment.url}>
                  <a
                    href={attachment.url}
                    download={attachment.filename}
                    class="attachment-thumb file"
                  >
                    <span>
                      {(attachment.filename.split(".").pop() ?? "file").slice(0, 4).toUpperCase()}
                    </span>
                  </a>
                  <div class="attachment-info">
                    <span class="attachment-name" title={attachment.filename}>
                      {attachment.filename}
                    </span>
                    <span class="muted small">{formatSize(attachment.size)}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
      {visibleAttachments.length > 0 && (
        <div class="attachments">
          <div class="attachments-head">
            <Icon name="paperclip" size={15} /> {visibleAttachments.length} attachment
            {visibleAttachments.length === 1 ? "" : "s"} ·{" "}
            {formatSize(visibleAttachments.reduce((sum, attachment) => sum + attachment.size, 0))}
          </div>
          <div class="attachment-grid">
            {visibleAttachments.map((attachment) => {
              const dangerous = attachment.scan?.status === "malicious";
              const view = dangerous
                ? `/api/attachments/${attachment.id}?download=1&confirm=1`
                : `/api/attachments/${attachment.id}`;
              const download = dangerous ? view : `/api/attachments/${attachment.id}?download=1`;
              const previewable = previewKind(attachment) !== null;
              // Files the browser can show open in the viewer, without leaving the app.
              const preview = (event: MouseEvent) => {
                if (!previewable || event.ctrlKey || event.metaKey || event.shiftKey) return;
                event.preventDefault();
                setViewing(previewables.indexOf(attachment));
              };
              // A file flagged as malicious only downloads after a clear warning.
              const guard = (event: MouseEvent) => {
                if (
                  dangerous &&
                  !window.confirm(
                    `${attachment.scan?.malicious} antivirus engines flag ${attachment.filename} as malicious. Opening it could harm your computer. Download it anyway?`,
                  )
                )
                  event.preventDefault();
              };
              return (
                <div class={`attachment${dangerous ? " dangerous" : ""}`} key={attachment.id}>
                  {attachment.contentType.startsWith("image/") && !dangerous ? (
                    <a
                      href={view}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="attachment-thumb"
                      onClick={preview}
                    >
                      <img src={view} alt="" loading="lazy" />
                    </a>
                  ) : (
                    <a
                      href={view}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="attachment-thumb file"
                      onClick={(event) => {
                        guard(event);
                        preview(event);
                      }}
                    >
                      <span>
                        {dangerous ? (
                          <Icon name="alert" size={18} />
                        ) : (
                          (attachment.filename.split(".").pop() ?? "file").slice(0, 4).toUpperCase()
                        )}
                      </span>
                    </a>
                  )}
                  <div class="attachment-info">
                    {previewable ? (
                      <a
                        class="attachment-name"
                        href={view}
                        title={`Preview ${attachment.filename}`}
                        onClick={preview}
                      >
                        {attachment.filename}
                      </a>
                    ) : (
                      <span class="attachment-name" title={attachment.filename}>
                        {attachment.filename}
                      </span>
                    )}
                    <span class="muted small">{formatSize(attachment.size)}</span>
                    {attachment.scan && (
                      <ScanBadge
                        scan={attachment.scan}
                        attachmentId={attachment.id}
                        filename={attachment.filename}
                        messageId={message.id}
                      />
                    )}
                  </div>
                  <a
                    class="icon-button"
                    href={download}
                    aria-label={`Download ${attachment.filename}`}
                    title="Download"
                    onClick={guard}
                  >
                    <Icon name="download" size={16} />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {viewing !== null && (
        <AttachmentViewer
          attachments={previewables}
          start={viewing}
          onClose={() => setViewing(null)}
        />
      )}
    </article>
  );
};

/**
 * What VirusTotal said about an attachment, linking to its full report. While a scan runs it
 * checks back for the result; a file VirusTotal doesn't know can be sent for analysis.
 */
/** Leaves the mailing list: in one click when the list supports it, else on its page or by email. */
const UnsubscribeButton = ({ message }: { message: MessageDetail }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  if (done) return <span class="unsubscribe muted small">Unsubscribed</span>;
  const run = async (event: MouseEvent) => {
    event.stopPropagation();
    setBusy(true);
    try {
      const result = await api.post<{
        done: boolean;
        url?: string;
        mailto?: { to: string; subject: string; body: string } | null;
      }>(`/messages/${message.id}/unsubscribe`, {});
      if (result.done) {
        setDone(true);
        toast(`Unsubscribed from ${displayName(message.from)}`);
      } else if (result.url) {
        window.open(result.url, "_blank", "noopener,noreferrer");
        toast("The sender's unsubscribe page opened in a new tab");
      } else if (result.mailto) {
        openComposer({
          mode: "new",
          sourceId: null,
          to: [result.mailto.to],
          subject: result.mailto.subject,
          ...(result.mailto.body ? { body: result.mailto.body } : {}),
        });
        toast("Send this email to unsubscribe");
      }
    } catch (error) {
      errorToast(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      class="unsubscribe link small"
      disabled={busy}
      title={
        message.unsubscribe === "one-click"
          ? "Leave this mailing list now"
          : message.unsubscribe === "link"
            ? "Open the sender's unsubscribe page"
            : "Email the list to leave it"
      }
      onClick={run}
    >
      {busy ? "Unsubscribing…" : "Unsubscribe"}
    </button>
  );
};

const ScanBadge = ({
  scan: initial,
  attachmentId,
  filename,
  messageId,
}: {
  scan: ScanResult;
  attachmentId: string;
  filename: string;
  messageId: string;
}) => {
  const [scan, setScan] = useState(initial);
  // Read-only members of a shared mailbox can't send its files off for scanning.
  const readOnly = useStore(
    app,
    (state) => state.mailboxes.find((entry) => entry.id === state.mailbox)?.role === "read",
  );
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => setScan(initial), [initial]);
  const pending = scan.status === "pending";
  useEffect(() => {
    if (!pending) return;
    return watchScans(messageId, (scans) => {
      const next = scans.get(attachmentId);
      if (next && next.status !== "pending") setScan(next);
    });
  }, [pending, messageId, attachmentId]);

  const flagged = scan.malicious + scan.suspicious;
  // Short enough for one line in the attachment card; the tooltip has the details.
  const [tone, text] =
    scan.status === "pending"
      ? ["", "Scanning…"]
      : scan.status === "clean"
        ? ["ok", "Clean"]
        : scan.status === "suspicious"
          ? ["warn", `Suspicious ${flagged}/${scan.engines}`]
          : scan.status === "malicious"
            ? ["danger", `Dangerous ${scan.malicious}/${scan.engines}`]
            : scan.status === "unknown"
              ? ["", "Unknown"]
              : ["", "Not scanned"];
  // VirusTotal only has a report for files it has analysed.
  const link = scan.engines > 0;
  const title =
    scan.engines > 0
      ? `${flagged} of ${scan.engines} antivirus engines on VirusTotal flag this file`
      : scan.status === "unknown"
        ? "VirusTotal has never seen this file, so it has no verdict yet"
        : scan.status === "pending"
          ? "Being checked with VirusTotal"
          : "Checked with VirusTotal";
  const scanNow = async () => {
    setSending(true);
    try {
      await api.post(`/attachments/${attachmentId}/scan`, {});
      setScan({ ...scan, status: "pending" });
      setConfirming(false);
      toast("Sent to VirusTotal; the result shows here in a few minutes");
    } catch (error) {
      errorToast(error);
    } finally {
      setSending(false);
    }
  };
  return (
    <span class="scan-line">
      {link ? (
        <a
          class={`scan-badge ${tone}`}
          href={scan.reportUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={`${title}. Open the report.`}
        >
          <Icon name="shield" size={12} /> {text}
        </a>
      ) : (
        <span class={`scan-badge ${tone}`} title={title}>
          <Icon name="shield" size={12} /> {text}
        </span>
      )}
      {!readOnly && (scan.status === "unknown" || scan.status === "error") && (
        <button type="button" class="link scan-now" onClick={() => setConfirming(true)}>
          Scan it
        </button>
      )}
      {confirming && (
        <Modal
          title="Upload this file to VirusTotal?"
          onClose={() => setConfirming(false)}
          footer={
            <>
              <button type="button" class="ghost" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button type="button" class="primary" disabled={sending} onClick={scanNow}>
                {sending ? "Uploading…" : "Upload and scan"}
              </button>
            </>
          }
        >
          <div class="scan-warning">
            <Icon name="shield" size={20} />
            <div>
              <p>
                VirusTotal has never seen <strong>{filename}</strong>, so it has to be uploaded for
                a full scan.
              </p>
              <p>
                Uploads are not private. VirusTotal keeps a copy and shares it with antivirus
                companies and security researchers, who can download and read it. It cannot be taken
                back once sent.
              </p>
              <p class="muted">
                Only upload files you would be fine with strangers reading. Don't upload contracts,
                invoices, IDs or anything else personal.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </span>
  );
};

const FRAME_STYLE = `
  html,body{margin:0;padding:0;background:#fff;color:#1f2937;}
  body{font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;padding:0;overflow-wrap:anywhere;}
  img{max-width:100%;height:auto;}
  pre{white-space:pre-wrap;}
  blockquote{margin:0 0 0 .5em;padding-left:1em;border-left:3px solid #d1d5db;color:#4b5563;}
  a{color:#2563eb;}
  table{max-width:100%;}
`;

/** An email with nothing to read (say, just an attachment) shows no body box at all. */
const isBlank = (source: { html: string | null; text: string }) => {
  if (source.html !== null) {
    if (/<(img|table|svg|video|iframe|hr)\b/i.test(source.html)) return false;
    const visible = source.html
      .replace(/<(style|script|head|title)\b[\s\S]*?<\/\1>/gi, "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;|&#160;|&#xa0;|&zwnj;|\u200b/gi, "");
    return visible.trim() === "";
  }
  return source.text.trim() === "";
};

/** Plain mail takes the app's own colors, so dark themes don't show a white box. */
const themedStyle = () => {
  const css = getComputedStyle(document.documentElement);
  const body = getComputedStyle(document.body);
  const value = (name: string) => css.getPropertyValue(name).trim();
  const scheme = document.documentElement.dataset.scheme === "dark" ? "dark" : "light";
  return `
  :root{color-scheme:${scheme};}
  html,body{background:transparent;color:${value("--text")};}
  body{font-family:${body.fontFamily};font-size:${body.fontSize};line-height:${body.lineHeight};}
  blockquote{border-left-color:${value("--border-strong")};color:${value("--text-2")};}
  a{color:${value("--accent")};}`;
};

/**
 * In a dark theme, text the sender colored near-black (and white boxes around it) would be
 * unreadable on the dark page; lighten them like other mail apps do.
 */
const adaptColors = (doc: Document) => {
  if (document.documentElement.dataset.scheme !== "dark") return;
  const text = getComputedStyle(document.documentElement).getPropertyValue("--text").trim();
  const view = doc.defaultView;
  if (!view) return;
  const channels = (color: string) => color.match(/[\d.]+/g)?.map(Number) ?? [];
  const designedQuotes = [...doc.querySelectorAll<HTMLElement>("details.cm-quoted")].filter(
    (quote) => hasOwnDesign(quote.innerHTML),
  );
  // Root inline white/black colors from mail clients need adaptation too.
  for (const element of [
    doc.documentElement,
    doc.body,
    ...doc.body.querySelectorAll<HTMLElement>("*"),
  ]) {
    if (designedQuotes.some((quote) => quote.contains(element))) continue;
    const style = view.getComputedStyle(element);
    const [r = 0, g = 0, b = 0] = channels(style.color);
    // Only grey and black text: a sender's red or blue still means something.
    const neutral = Math.max(r, g, b) - Math.min(r, g, b) < 40;
    if (!element.closest("a") && neutral && (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.45)
      element.style.setProperty("color", text, "important");
    const [br = 0, bg = 0, bb = 0, alpha = 1] = channels(style.backgroundColor);
    if (alpha > 0 && br > 240 && bg > 240 && bb > 240)
      element.style.setProperty("background-color", "transparent", "important");
  }
};

const buildFrameDocument = (
  message: MessageDetail,
  allowRemote: boolean,
  unlocked: ReaderContent | null,
  themed: boolean,
): string => {
  // Remote images arrive only through the Worker's proxy (/api/proxy); nothing else remote loads.
  const origin = location.origin;
  const images = `${origin}/api/attachments/${allowRemote ? ` ${origin}/api/proxy` : ""}`;
  const csp = `default-src 'none'; img-src data: blob: ${images}; style-src 'unsafe-inline'; font-src data:`;
  const source = unlocked ?? message;
  const body =
    source.html === null ? foldTextQuote(source.text) : foldHtmlQuote(defuseHtml(source.html));
  const canvas = source.html === null || themed ? null : emailCanvasColor(source.html);
  return `<!doctype html><html${canvas ? ` style="background-color:${canvas}"` : ""}><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}">${NO_DNS_PREFETCH}<base target="_blank"><style>${FRAME_STYLE}${QUOTE_STYLE}${themed ? themedStyle() : ""}</style></head><body>${body}</body></html>`;
};

const MessageBody = ({
  message,
  allowRemote,
  unlocked,
}: {
  message: MessageDetail;
  allowRemote: boolean;
  unlocked: ReaderContent | null;
}) => {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(120);
  const themed = useThemed(message, unlocked);
  const look = useLook();
  // Folding the quote parses the whole email, so it runs once per look, not on every render.
  const srcdoc = useMemo(
    () => buildFrameDocument(message, allowRemote, unlocked, themed),
    [message.id, allowRemote, unlocked, themed, look],
  );
  // The frame shows through only when it and the page agree on light or dark; otherwise the
  // browser paints the frame its own solid color, whatever the theme.
  const scheme = themed && document.documentElement.dataset.scheme === "dark" ? "dark" : "light";
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    let observer: ResizeObserver | null = null;
    const measure = () => {
      const doc = element.contentDocument;
      if (!doc?.documentElement) return;
      // A short reply should size to its content, not the iframe's initial viewport.
      const contentHeight =
        themed && doc.body ? doc.body.scrollHeight : doc.documentElement.scrollHeight;
      setHeight(Math.max(60, contentHeight + 8));
    };
    const onLoad = () => {
      if (themed && element.contentDocument?.body) adaptColors(element.contentDocument);
      measure();
      const doc = element.contentDocument;
      if (doc?.body) {
        observer = new ResizeObserver(measure);
        observer.observe(doc.body);
        // Images load after the frame; measure again as each one arrives.
        for (const image of doc.images) image.addEventListener("load", measure);
      }
    };
    element.addEventListener("load", onLoad);
    // A short srcdoc can finish loading before this effect runs.
    if (
      element.contentDocument?.readyState === "complete" &&
      element.contentDocument.body?.firstChild
    )
      onLoad();
    return () => {
      element.removeEventListener("load", onLoad);
      observer?.disconnect();
    };
  }, [srcdoc, themed]);
  return (
    <iframe
      ref={frame}
      class={`message-frame${themed ? " themed" : ""}`}
      title={`Message from ${formatAddress(message.from)}`}
      // No allow-scripts: mail never runs code. allow-same-origin only lets us size the frame.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerpolicy="no-referrer"
      srcdoc={srcdoc}
      style={{ height: `${height}px`, colorScheme: scheme }}
    />
  );
};
